import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { WelletContext } from '../../../context/wellet/welletContext';
import { getSpecialDateProducts } from '../../../services';
import { Modal, Loading, Tabs } from '../../../uiComponents/common';
import { CheckBox } from '../../../uiComponents/common/checkbox/checkbox';
import SearchIcon from '../../../uiComponents/icons/searchIcon';
import CheckIcon from '../../../uiComponents/icons/checkIcon';
import PlusIcon from '../../../uiComponents/icons/plusIcon';
import { defaultQueryOptions } from '../../../utils/defaultQueryOptions';

const AddProductsModal = ({ open, setOpen, linkedProductIds = [], onConfirm, isLoading, restaurantId }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.fechasEspeciales.detail.sectionProducts' });
  const welletContext = useContext(WelletContext);
  const [activeTab, setActiveTab] = useState('area');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  const tabs = [
    { type: 'area', title: t('tabArea'), isActive: activeTab === 'area' },
    { type: 'group', title: t('tabGroup'), isActive: activeTab === 'group' },
  ];

  const productsQuery = useQuery({
    queryKey: ['specialDateProducts', restaurantId, activeTab],
    queryFn: () =>
      getSpecialDateProducts(
        welletContext,
        restaurantId,
        activeTab === 'area' ? 'AREA' : 'GROUP',
      ),
    ...defaultQueryOptions,
    enabled: open && !!restaurantId,
  });

  const currentProducts = productsQuery?.data?.data || [];
  const currentLoading = productsQuery.isLoading;

  const availableProducts = useMemo(() => {
    return currentProducts
      .filter((p) => !linkedProductIds.includes(p.productId))
      .filter((p) => !search || p.productName?.toLowerCase().includes(search.toLowerCase()));
  }, [currentProducts, linkedProductIds, search]);

  const hasAddedAll =
    !currentLoading && currentProducts.length > 0 && currentProducts.every((p) => linkedProductIds.includes(p.productId));

  const handleToggle = (productId) => {
    setSelected((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  };

  const handleSelectAll = () => {
    const allFilteredIds = availableProducts.map((p) => p.productId);
    setSelected(allFilteredIds);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected([]);
    setActiveTab('area');
    setSearch('');
  };

  const handleConfirm = () => {
    const products = selected.map((id) => {
      const product = currentProducts.find((p) => p.productId === id);
      return {
        productId: product.productId,
        productName: product.productName,
        reservationType: product.reservationType,
        prices: product.prices || [],
      };
    });
    onConfirm(products, activeTab);
    handleClose();
  };

  return (
    <Modal
      title={t('addProductTitle')}
      open={open}
      onClose={handleClose}
      maxWidth='550px'
      customButtons={
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div className='fe-modal-selected-count'>
            {selected.length > 0 && t('addProductSelected', { count: selected.length })}
          </div>
          <div className='d-flex gap-2'>
            <button
              type='button'
              onClick={handleClose}
              className='btn-general btn-wellet-secondary'
              disabled={isLoading}
            >
              {t('addProductCancel')}
            </button>
            <button
              type='button'
              onClick={handleConfirm}
              className='btn-general btn-wellet-primary ml-2'
              disabled={selected.length === 0 || isLoading}
            >
              <PlusIcon size='12px' color='currentColor' />
              <span className='ml-1'>{t('addProductConfirm')} ({selected.length})</span>
            </button>
          </div>
        </div>
      }
    >
      <div className='fe-modal-description mb-3'>{t('addProductDescription')}</div>

      <div className='mb-3'>
        <Tabs options={tabs} onClick={(type) => { setActiveTab(type); setSearch(''); setSelected([]); }} />
      </div>

      <div className='d-flex align-items-center mb-3'>
        <div className='fe-modal-search-wrapper'>
          <div className='fe-modal-search-icon'>
            <SearchIcon size='16px' color='#98A2B3' />
          </div>
          <input
            type='text'
            className='form-control fe-modal-search-input'
            placeholder={t('addProductSearch')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {availableProducts.length > 0 && (
          <button
            type='button'
            className='btn-general btn-wellet-secondary fe-modal-select-all'
            onClick={handleSelectAll}
          >
            {t('addProductSelectAll')}
          </button>
        )}
      </div>

      {currentLoading ? (
        <div className='fe-modal-empty d-flex align-items-center justify-content-center'>
          <Loading global={false} />
        </div>
      ) : availableProducts.length === 0 ? (
        <div className='fe-modal-empty'>
          <div className='fe-modal-empty-icon'>
            <SearchIcon size='24px' color='#98A2B3' />
          </div>
          <div className='fe-modal-empty-text'>
            {hasAddedAll ? t('addProductAllAdded') : t('addProductEmpty')}
          </div>
        </div>
      ) : (
        <div className='fe-modal-product-list'>
          {availableProducts.map((product) => {
            const isSelected = selected.includes(product.productId);
            return (
              <label
                key={product.productId}
                className={`fe-modal-product-row ${isSelected ? 'fe-modal-product-row-selected' : ''}`}
              >
                <CheckBox
                  checked={isSelected}
                  handleCheck={() => handleToggle(product.productId)}
                  label={<span className='fw-600'>{product.productName}</span>}
                />
                {isSelected && (
                  <span className='fe-modal-check-icon'>
                    <CheckIcon size='14px' color='var(--color-primary, #6941C6)' />
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default AddProductsModal;
